import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getAdminInvoices, getBusiness } from '../../services/api';
import BusinessSearchSelect from '../../components/BusinessSearchSelect';
import { FileText, Search, X, Eye, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';

const CFDI_TYPES = {
  I: { label: 'Ingreso', color: 'bg-blue-100 text-blue-700' },
  E: { label: 'Egreso', color: 'bg-orange-100 text-orange-700' },
  P: { label: 'Pago', color: 'bg-purple-100 text-purple-700' },
  T: { label: 'Traslado', color: 'bg-slate-100 text-slate-600' },
};

const STATUS_LABELS = {
  active: { label: 'Activa', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  cancel_pending: { label: 'Pend. Cancelar', color: 'bg-yellow-100 text-yellow-700' },
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const FacturacionAdmin = () => {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 30;
  const [filters, setFilters] = useState({
    business_id: '', status_filter: '', cfdi_type: '', rfc_receptor: '', q: '', date_from: '', date_to: '',
  });
  const [showDetail, setShowDetail] = useState(null);
  const [businessCache, setBusinessCache] = useState({});

  useEffect(() => { setPage(0); }, [filters]);

  useEffect(() => { loadInvoices(); }, [page, filters]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await getAdminInvoices(params);
      setInvoices(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error loading invoices:', err);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const getBusinessName = (id) => {
    if (!id) return '—';
    if (businessCache[id]) return businessCache[id];
    getBusiness(id).then(r => setBusinessCache(prev => ({ ...prev, [id]: r.data.name }))).catch(() => {});
    return id.slice(0, 8) + '...';
  };

  const copyUUID = (uuid) => {
    navigator.clipboard.writeText(uuid);
    toast.success('UUID copiado');
  };

  const clearFilters = () => {
    setFilters({ business_id: '', status_filter: '', cfdi_type: '', rfc_receptor: '', q: '', date_from: '', date_to: '' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Facturación</h1>
          <p className="text-slate-600 mt-2">Monitoreo de CFDIs timbrados en todas las licencias</p>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-slate-200 space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-64">
              <label className="block text-xs font-medium text-slate-500 mb-1">Negocio</label>
              <BusinessSearchSelect
                value={filters.business_id}
                onChange={(id) => setFilters({ ...filters, business_id: id })}
                placeholder="Todos..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Estado</label>
              <select value={filters.status_filter} onChange={(e) => setFilters({ ...filters, status_filter: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
                <option value="">Todos</option>
                <option value="active">Activa</option>
                <option value="cancelled">Cancelada</option>
                <option value="cancel_pending">Pend. Cancelar</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Tipo CFDI</label>
              <select value={filters.cfdi_type} onChange={(e) => setFilters({ ...filters, cfdi_type: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
                <option value="">Todos</option>
                <option value="I">Ingreso</option>
                <option value="E">Egreso</option>
                <option value="P">Pago</option>
                <option value="T">Traslado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">RFC Receptor</label>
              <input type="text" value={filters.rfc_receptor} onChange={(e) => setFilters({ ...filters, rfc_receptor: e.target.value.toUpperCase() })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 w-36" placeholder="RFC..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Buscar</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <input type="text" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 w-48" placeholder="UUID, nombre, folio..." />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Desde</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Hasta</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
            </div>
            {Object.values(filters).some(v => v) && (
              <button onClick={clearFilters} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
                <X className="w-3.5 h-3.5" /> Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <FileText className="w-4 h-4" />
          <span>{total.toLocaleString('es-MX')} facturas encontradas</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">UUID Fiscal</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Serie-Folio</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Receptor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.length === 0 ? (
                    <tr><td colSpan="9" className="px-4 py-12 text-center text-slate-500">No se encontraron facturas</td></tr>
                  ) : invoices.map((inv) => {
                    const ct = CFDI_TYPES[inv.type] || { label: inv.type, color: 'bg-slate-100 text-slate-600' };
                    const st = STATUS_LABELS[inv.status] || { label: inv.status, color: 'bg-slate-100 text-slate-600' };
                    return (
                      <tr key={inv.id || inv.uuid_fiscal} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <code className="text-xs font-mono text-slate-600" title={inv.uuid_fiscal}>{inv.uuid_fiscal?.slice(0, 8)}...</code>
                            <button onClick={() => copyUUID(inv.uuid_fiscal)} className="p-0.5 hover:bg-slate-100 rounded"><Copy className="w-3 h-3 text-slate-400" /></button>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ct.color}`}>{ct.label}</span></td>
                        <td className="px-4 py-3 text-sm text-slate-700">{inv.serie || ''}{inv.folio ? `-${inv.folio}` : ''}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-700 truncate max-w-[150px]">{inv.nombre_receptor || '—'}</p>
                          <p className="text-xs text-slate-400">{inv.rfc_receptor}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{getBusinessName(inv.business_id)}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">${(inv.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span></td>
                        <td className="px-4 py-3 text-sm text-slate-500">{formatDate(inv.stamped_at || inv.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setShowDetail(inv)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Ver detalle">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Página {page + 1} de {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-1.5 hover:bg-slate-100 rounded disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages} className="p-1.5 hover:bg-slate-100 rounded disabled:opacity-30">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-slate-900">Detalle de Factura</h2>
              <button onClick={() => setShowDetail(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                <code className="text-sm font-mono text-slate-800 flex-1 break-all">{showDetail.uuid_fiscal}</code>
                <button onClick={() => copyUUID(showDetail.uuid_fiscal)} className="p-1.5 hover:bg-slate-200 rounded"><Copy className="w-4 h-4 text-slate-600" /></button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Tipo</label>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${(CFDI_TYPES[showDetail.type] || {}).color || 'bg-slate-100'}`}>
                    {(CFDI_TYPES[showDetail.type] || {}).label || showDetail.type}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Estado</label>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${(STATUS_LABELS[showDetail.status] || {}).color || 'bg-slate-100'}`}>
                    {(STATUS_LABELS[showDetail.status] || {}).label || showDetail.status}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Serie - Folio</label>
                  <p className="text-sm font-medium text-slate-900">{showDetail.serie || '—'} - {showDetail.folio || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Moneda</label>
                  <p className="text-sm text-slate-900">{showDetail.moneda || 'MXN'}</p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <label className="block text-xs font-medium text-slate-500 mb-2">Emisor</label>
                <p className="text-sm text-slate-900">{showDetail.rfc_emisor}</p>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <label className="block text-xs font-medium text-slate-500 mb-2">Receptor</label>
                <p className="text-sm font-medium text-slate-900">{showDetail.nombre_receptor}</p>
                <p className="text-sm text-slate-600">{showDetail.rfc_receptor}</p>
              </div>

              <div className="border-t border-slate-100 pt-4 grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Subtotal</label>
                  <p className="text-sm font-medium text-slate-900">${(showDetail.subtotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">IVA</label>
                  <p className="text-sm font-medium text-slate-900">${(showDetail.iva || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Total</label>
                  <p className="text-lg font-bold text-teal-700">${(showDetail.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Timbrado</label>
                  <p className="text-sm text-slate-900">{formatDate(showDetail.stamped_at)}</p>
                </div>
                {showDetail.cancelled_at && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Cancelado</label>
                    <p className="text-sm text-red-600">{formatDate(showDetail.cancelled_at)}</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Origen</label>
                  <p className="text-sm text-slate-900">{showDetail.origin || 'pos_desktop'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Negocio</label>
                  <p className="text-sm text-slate-900">{getBusinessName(showDetail.business_id)}</p>
                </div>
              </div>

              <div className="pt-2">
                <p className="text-xs text-slate-400">ID: {showDetail.id}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default FacturacionAdmin;
